import type { SearchActionRow } from "~/helpers/search/deriveSearchActionRows";
import type { EntitySearchIntent } from "~/helpers/search/entitySearchQuery";
import type { SearchResultRow, SearchSection } from "~/layout/useEntitySearch";

import {
	Box,
	Divider,
	Icon,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	Skeleton,
	useMediaQuery
} from "@mui/material";
import { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import DidYouMeanLink from "~/components/DidYouMeanLink";
import Keycap from "~/components/keyboard/Keycap";
import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import { highlightSegments } from "~/helpers/search/labelMatcher";
import useKeybindingOverrides from "~/hooks/useKeybindingOverrides";
import hintBadgeBinding from "~/keyboard/hintBadgeBinding";

const HighlightedLabel = ({ label, query }: { label: string; query: string }) => (
	<BodyTextSmall noWrap>
		{highlightSegments(label, query).map((segment, index) => (
			<Box
				component="span"
				key={`${index}-${segment.text}`}
				sx={{ color: segment.match ? "text.primary" : "text.secondary", fontWeight: segment.match ? 700 : 400 }}
			>
				{segment.text}
			</Box>
		))}
	</BodyTextSmall>
);

interface SearchResultsListProps {
	actionRows: SearchActionRow[];
	activeIndex?: number;
	getOptionId?: (index: number) => string;
	intent: EntitySearchIntent;
	isLoadingResults: boolean;
	itemSuggestion?: null | string;
	listboxId?: string;
	navigableRows: SearchResultRow[];
	onRunAction?: (row: SearchActionRow) => void;
	onSelectRow: (row: SearchResultRow) => void;
	onSuggestionSelect?: (suggestion: string) => void;
	query: string;
	rowMinHeight?: number;
	sections: SearchSection[];
	showActionKeyHint?: boolean;
}

const SearchResultsList = ({
	actionRows,
	activeIndex = -1,
	getOptionId,
	intent,
	isLoadingResults,
	itemSuggestion,
	listboxId,
	navigableRows,
	onRunAction,
	onSelectRow,
	onSuggestionSelect,
	query,
	rowMinHeight,
	sections,
	showActionKeyHint = false
}: SearchResultsListProps) => {
	const { t } = useTranslation("common");
	const { overrides } = useKeybindingOverrides();
	const highlightQuery = intent.query || query;
	const rowSx = rowMinHeight ? { minHeight: rowMinHeight } : undefined;
	const scopeLabel = intent.command
		? t("search.scope.commands")
		: intent.explicit && intent.entity
			? t("search.scope.searching", { entity: t(`search.entities.${intent.entity}`) })
			: null;
	const noMatchTerm = intent.explicit ? intent.query : query;
	const activeRowRef = useRef<HTMLDivElement>(null);
	const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

	useEffect(() => {
		if (activeIndex < 0) {
			return;
		}
		activeRowRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "nearest" });
	}, [activeIndex, prefersReducedMotion]);

	return (
		<>
			{scopeLabel && (
				<Box
					sx={{
						alignItems: "center",
						bgcolor: "action.hover",
						borderBottom: 1,
						borderColor: "divider",
						display: "flex",
						gap: 0.75,
						px: 2,
						py: 0.75
					}}
				>
					<Icon fontSize="small" sx={{ color: "primary.main" }}>
						search
					</Icon>
					<CaptionText sx={{ color: "text.secondary", fontWeight: 600 }}>{scopeLabel}</CaptionText>
				</Box>
			)}
			<List
				aria-label={t("search.resultsAria")}
				dense
				disablePadding
				id={listboxId}
				role={listboxId ? "listbox" : undefined}
			>
				{sections.map((section) => (
					<Fragment key={section.key}>
						<ListSubheader disableSticky sx={{ bgcolor: "transparent", lineHeight: 2.2 }}>
							<CaptionText sx={{ color: "text.secondary", fontWeight: 600 }}>{section.title}</CaptionText>
						</ListSubheader>
						{section.rows.map((row) => {
							const index = navigableRows.indexOf(row);
							const selected = index === activeIndex;
							const shortcutBinding = row.shortcutActionId ? hintBadgeBinding(row.shortcutActionId, overrides) : null;
							return (
								<ListItemButton
									aria-selected={listboxId ? selected : undefined}
									id={getOptionId ? getOptionId(index) : undefined}
									key={row.key}
									onClick={() => onSelectRow(row)}
									ref={selected ? activeRowRef : undefined}
									role={listboxId ? "option" : undefined}
									selected={selected}
									sx={rowSx}
								>
									<ListItemIcon sx={{ minWidth: 34 }}>
										<Icon fontSize="small" sx={{ color: row.color }}>
											{row.icon}
										</Icon>
									</ListItemIcon>
									<ListItemText
										primary={<HighlightedLabel label={row.label} query={highlightQuery} />}
										secondary={
											row.secondary ? (
												<CaptionText noWrap sx={{ color: "text.secondary", display: "block" }}>
													{row.secondary}
												</CaptionText>
											) : undefined
										}
									/>
									{shortcutBinding && (
										<Box sx={{ flexShrink: 0, ml: 1 }}>
											<Keycap binding={shortcutBinding} />
										</Box>
									)}
								</ListItemButton>
							);
						})}
						{section.loading &&
							Array.from({ length: section.skeletonCount }).map((_, skeletonIndex) => (
								<ListItem key={`${section.key}-skeleton-${skeletonIndex}`}>
									<ListItemIcon sx={{ minWidth: 34 }}>
										<Skeleton height={20} variant="circular" width={20} />
									</ListItemIcon>
									<ListItemText primary={<Skeleton width="60%" />} secondary={<Skeleton width="40%" />} />
								</ListItem>
							))}
					</Fragment>
				))}
				{noMatchTerm && navigableRows.length === 0 && !isLoadingResults && (
					<ListItem>
						<ListItemText
							primary={
								<CaptionText noWrap sx={{ color: "text.secondary", display: "block" }}>
									{t("search.noMatches", { term: noMatchTerm })}
								</CaptionText>
							}
						/>
					</ListItem>
				)}
				{itemSuggestion && onSuggestionSelect && (
					<ListItem>
						<DidYouMeanLink onSelect={onSuggestionSelect} suggestion={itemSuggestion} />
					</ListItem>
				)}
			</List>
			{actionRows.length > 0 && (
				<>
					<Divider />
					<List aria-label={t("search.actionsAria")} dense disablePadding>
						{actionRows.map((row) => (
							<ListItemButton key={row.key} onClick={() => onRunAction?.(row)} sx={rowSx}>
								<ListItemIcon sx={{ minWidth: 34 }}>
									<Icon fontSize="small" sx={{ color: "primary.main" }}>
										{row.icon}
									</Icon>
								</ListItemIcon>
								<ListItemText primary={<BodyTextSmall noWrap>{row.label}</BodyTextSmall>} />
								{showActionKeyHint && row.kind === "filterGrid" && (
									<Box
										component="span"
										sx={{
											bgcolor: "action.hover",
											border: 1,
											borderColor: "divider",
											borderRadius: 0.75,
											color: "text.secondary",
											fontFamily: "monospace",
											fontSize: "0.7rem",
											lineHeight: 1.4,
											px: 0.5
										}}
									>
										Alt ⏎
									</Box>
								)}
							</ListItemButton>
						))}
					</List>
				</>
			)}
		</>
	);
};

export default SearchResultsList;
